import { useId, useRef, useState } from "react";

import { mergeOptionen } from "@repo/shared";

import { Popover } from "./Popover";

type ListeProps = {
  optionen: string[];
  gewaehlt: string[];
  onChange(werte: string[]): void;
  /** Legt einen neuen Wert an und liefert seinen Namen; `null` = nicht angelegt. Ohne: kein Eingabefeld. */
  neuerWert?: (name: string) => Promise<string | null>;
};

const gleich = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Häkchen-Liste der Werte, darunter optional „Neuen Wert anlegen“. Gewählte Werte, die (noch)
 * nicht in den Optionen stehen, erscheinen trotzdem – sonst ließen sie sich nicht abwählen.
 */
export function AuswahlListe({ optionen, gewaehlt, onChange, neuerWert }: ListeProps) {
  const [zusatz, setZusatz] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const feldId = useId();

  // Nach dem Anlegen zählt die Auswahl von dann, nicht die beim Klick.
  const gewaehltRef = useRef(gewaehlt);
  gewaehltRef.current = gewaehlt;

  const liste = mergeOptionen(optionen, [...gewaehlt, ...zusatz]);
  const istGewaehlt = (wert: string) => gewaehlt.some((g) => gleich(g, wert));

  function umschalten(wert: string) {
    onChange(istGewaehlt(wert) ? gewaehlt.filter((g) => !gleich(g, wert)) : [...gewaehlt, wert]);
  }

  function anhaken(wert: string) {
    const aktuell = gewaehltRef.current;
    if (!aktuell.some((g) => gleich(g, wert))) onChange([...aktuell, wert]);
  }

  async function hinzufuegen() {
    const name = text.trim();
    if (!name || laeuft) return;
    // Gibt es den Wert schon (ohne Groß-/Kleinschreibung), wird er nur angehakt.
    const vorhanden = liste.find((o) => gleich(o, name));
    if (vorhanden) {
      anhaken(vorhanden);
      setText("");
      return;
    }
    if (!neuerWert) return;
    setLaeuft(true);
    try {
      const angelegt = await neuerWert(name);
      if (angelegt) {
        setZusatz((z) => [...z, angelegt]);
        anhaken(angelegt);
        setText("");
      }
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="stoffe-auswahl-liste">
      {liste.length === 0 ? (
        <p className="muted small stoffe-auswahl-liste__leer">Keine Werte vorhanden.</p>
      ) : (
        <div className="stoffe-auswahl-liste__werte">
          {liste.map((wert) => (
            <label key={wert} className="stoffe-auswahl-liste__wert">
              <input type="checkbox" checked={istGewaehlt(wert)} onChange={() => umschalten(wert)} />
              {wert}
            </label>
          ))}
        </div>
      )}
      {neuerWert && (
        <div className="stoffe-auswahl-liste__neu">
          <label className="small muted" htmlFor={feldId}>
            Neuen Wert anlegen
          </label>
          <div className="stoffe-auswahl-liste__eingabe">
            <input
              id={feldId}
              type="text"
              value={text}
              maxLength={100}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                // Kein Absenden eines umgebenden Formulars – Enter heißt hier „Hinzufügen“.
                if (e.key === "Enter") {
                  e.preventDefault();
                  void hinzufuegen();
                }
              }}
            />
            <button type="button" disabled={!text.trim() || laeuft} onClick={() => void hinzufuegen()}>
              Hinzufügen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

type Props = ListeProps & {
  platzhalter: string;
  /** Name für Screenreader, wenn kein `<label htmlFor>` auf `id` zeigt. */
  label?: string;
  id?: string;
  /** Wahlweise von außen gesteuert (z. B. Öffnen über das Filter-Häkchen). */
  offen?: boolean;
  onOffen?(offen: boolean): void;
};

/** Mehrfachauswahl als Knopf mit Popover: zeigt die gewählten Werte oder den Platzhalter. */
export function MehrfachAuswahl({ platzhalter, label, id, offen, onOffen, ...liste }: Props) {
  const [eigenOffen, setEigenOffen] = useState(false);
  const istOffen = offen ?? eigenOffen;
  const setzeOffen = onOffen ?? setEigenOffen;
  const text = liste.gewaehlt.join(", ");

  return (
    <Popover
      offen={istOffen}
      onOffen={setzeOffen}
      label={label ?? platzhalter}
      className="stoffe-popover__inhalt--auswahl"
      ausloeser={
        <button
          id={id}
          type="button"
          className="stoffe-auswahl"
          aria-haspopup="dialog"
          aria-expanded={istOffen}
          aria-label={label ? `${label}: ${text || platzhalter}` : undefined}
          title={text || undefined}
        >
          <span className={text ? "stoffe-auswahl__text" : "stoffe-auswahl__text stoffe-auswahl__text--leer"}>
            {text || platzhalter}
          </span>
        </button>
      }
    >
      <AuswahlListe {...liste} />
    </Popover>
  );
}
